import React from 'react';
import JSONTreeViewer from '../../../../components/JSONTreeViewer';
import { typography, palette } from '../../../../utils/designTokens';
import { logger } from '../../../../utils/logger';

/**
 * JSONCollapsibleViewer - Displays formatted JSON data with tree navigation
 * Used for invocation parameters, tool schemas, and other JSON content in trace detail tabs
 *
 * @param {Object|string} data - JSON data to display
 * @param {string} label - Section label (optional)
 * @param {boolean} collapsible - Whether to wrap in a collapsible details element (default: false)
 * @param {number} initialExpandDepth - How many tree levels to expand initially (default: 0)
 * @param {string} maxHeight - Max height with scroll (default: '400px')
 * @param {boolean} showControls - Show expand/collapse all buttons (default: true)
 */
const JSONCollapsibleViewer = ({
  data,
  label,
  collapsible = false,
  initialExpandDepth = 0,
  maxHeight = '400px',
  showControls = true
}) => {
  if (!data || (typeof data === 'object' && Object.keys(data).length === 0)) {
    return null;
  }

  logger.debug('JSONCollapsibleViewer rendering:', { label, dataType: typeof data });

  const content = (
    <JSONTreeViewer
      data={data}
      initialExpandDepth={initialExpandDepth}
      maxHeight={maxHeight}
      showControls={showControls}
    />
  );

  if (collapsible && label) {
    return (
      <details className="space-y-2">
        <summary
          className="cursor-pointer font-medium hover:underline"
          style={{
            fontSize: typography.size.sm,
            color: palette.text.secondary
          }}
        >
          {label}
        </summary>
        {content}
      </details>
    );
  }

  return (
    <div className="space-y-2">
      {label && (
        <div
          className="font-medium"
          style={{
            fontSize: typography.size.sm,
            color: palette.text.secondary
          }}
        >
          {label}
        </div>
      )}
      {content}
    </div>
  );
};

export default JSONCollapsibleViewer;
